import React, { useState, useMemo, useEffect } from 'react';
import { User, CheckIn, CalendarEvent, AuthUser } from '../types';
import { getMonthCalendarEvents } from '../firebase/firestore';
import * as XLSX from 'xlsx';

interface AdminAttendanceTimesheetProps {
  users: User[];
  checkIns: CheckIn[];
  currentUser: AuthUser;
}

interface DayData {
  date: Date;
  isToday: boolean;
  isCurrentMonth: boolean;
  checkIns: CheckIn[];
  events: CalendarEvent[];
}

interface UserSummary {
  userId: string;
  userName: string;
  userEmail: string;
  totalDays: number;
  workDays: number;
  offDays: number;
  resmiTatil: number;
  izin: number;
  raporlu: number;
  presentDays: number;
  absentDays: number;
}

interface YearlyUserSummary {
  userId: string;
  userName: string;
  userEmail: string;
  toplamGun: number;
  calismaGunu: number;
  geldi: number;
  gelmedi: number;
  resmiTatil: number;
  hakEdilenIzin: number;
  kullanilanIzin: number;
  kalanIzin: number;
  raporlu: number;
}

const AdminAttendanceTimesheet: React.FC<AdminAttendanceTimesheetProps> = ({ users, checkIns }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [showSummary] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'aylik' | 'yillik'>('aylik');

  // Load calendar events for the current month
  useEffect(() => {
    if (viewMode === 'aylik') {
      loadCalendarEvents();
    }
  }, [currentDate, viewMode]);

  const loadCalendarEvents = async () => {
    try {
      setLoadingEvents(true);
      const events = await getMonthCalendarEvents(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1
      );
      console.log('Loaded calendar events:', events);
      console.log('Current month:', currentDate.getFullYear(), currentDate.getMonth() + 1);
      setCalendarEvents(events);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  // Load all calendar events for the year
  const loadYearlyCalendarEvents = async () => {
    try {
      setLoadingEvents(true);
      const allEvents: CalendarEvent[] = [];
      
      // Load events for all months of the year
      for (let month = 1; month <= 12; month++) {
        try {
          const monthEvents = await getMonthCalendarEvents(currentDate.getFullYear(), month);
          allEvents.push(...monthEvents);
        } catch (error) {
          console.error(`Error loading events for month ${month}:`, error);
        }
      }
      
      setCalendarEvents(allEvents);
    } catch (error) {
      console.error('Error loading yearly calendar events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  // Load yearly events when view mode changes to yillik
  useEffect(() => {
    if (viewMode === 'yillik') {
      loadYearlyCalendarEvents();
    }
  }, [viewMode, currentDate.getFullYear()]);

  // Function to mark user as "came" for a specific day
  /*const markUserAsCame = async (date: Date, userId: string) => {
    try {
      const dateStr = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      const eventData = {
        type: 'geldi' as const,
        title: 'Geldi',
        description: 'Admin tarafindan eklendi',
        startDate: dateStr,
        endDate: dateStr,
        isAllDay: true,
        scope: 'user_specific' as const,
        userId: userId
      };
      
      await createCalendarEvent(eventData, currentUser.uid);
      
      // Reload calendar events to reflect the change
      await loadCalendarEvents();
    } catch (error) {
      console.error('Error marking user as came:', error);
      alert('Hata: Kullanıcı "geldi" olarak işaretlenemedi.');
    }
  };*/

  // Function to remove "geldi" event (undo marking user as came)
  /*const removeUserCame = async (date: Date, userId: string) => {
    try {
      // Find the "geldi" event for this user and date
      const dayEvents = getEventsForDay(date, userId);
      const geldiEvent = dayEvents.find(event => event.type === 'geldi');
      
      if (geldiEvent) {
        await deleteCalendarEvent(geldiEvent.id);
        
        // Reload calendar events to reflect the change
        await loadCalendarEvents();
      }
    } catch (error) {
      console.error('Error removing user came status:', error);
      alert('Hata: Kullanıcının "geldi" durumu kaldırılamadı.');
    }
  };*/

  // Get the first day of the current month view
  const firstDayOfMonth = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  }, [currentDate]);

  // Get the last day of the current month view
  const lastDayOfMonth = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  }, [currentDate]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    // First filter out admin users and disabled users
    const activeUsers = users.filter(user => 
      user.id !== 'admin' && 
      user.email !== 'admin@mecon.com' && 
      user.email !== 'muhasebe@meconyapi.com' &&
      !user.disabled
    );

    if (!searchTerm.trim()) {
      return activeUsers;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    return activeUsers.filter(user => {
      const nameMatch = user.name.toLowerCase().includes(searchLower);
      const emailMatch = user.email.toLowerCase().includes(searchLower);
      const locationMatch = user.workLocation.name?.toLowerCase().includes(searchLower) || false;
      const addressMatch = user.workLocation.address?.toLowerCase().includes(searchLower) || false;
      
      return nameMatch || emailMatch || locationMatch || addressMatch;
    });
  }, [users, searchTerm]);

  // Calculate user summaries
  const userSummaries = useMemo(() => {
    if (loadingEvents) return [];

    const summaries: UserSummary[] = [];
    const totalDays = lastDayOfMonth.getDate();

    filteredUsers.forEach(user => {
      let offDays = 0;
      let resmiTatil = 0;
      let izin = 0;
      let raporlu = 0;
      let presentDays = 0;
      let absentDays = 0;

      // Check each day of the month
      for (let day = 1; day <= totalDays; day++) {
        const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        
        // Get events for this day and user
        const dayEvents = calendarEvents.filter(event => {
          const eventStart = new Date(event.startDate);
          const eventEnd = new Date(event.endDate);
          
          // Reset time to start of day for proper comparison
          const startOfDay = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
          const startOfEventStart = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
          const startOfEventEnd = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
          
          const isOnDate = startOfDay >= startOfEventStart && startOfDay <= startOfEventEnd;
          const isForUser = event.scope === 'company_wide' || event.userId === user.id;
          
          // For Saturday events, only apply to Merkez Ofis users
          const isSaturday = currentDay.getDay() === 6;
          const isMerkezOfis = user.workLocation.name === 'Merkez Ofis';
          const isSaturdayEvent = isSaturday && eventStart.getDay() === 6;
          
          if (isSaturdayEvent && !isMerkezOfis) {
            return false; // Skip Saturday events for non-Merkez Ofis users
          }
          
          return isOnDate && isForUser;
        });

        // Get check-ins for this day and user (only first check-in of the day)
        const dayCheckIns = checkIns.filter(checkIn => {
          const checkInDate = new Date(checkIn.timestamp);
          return checkInDate.toDateString() === currentDay.toDateString() && checkIn.userId === user.id;
        });
        
        // Only count the first check-in of the day for this user
        const hasCheckIn = dayCheckIns.length > 0;

        // Count events by type with priority: resmi-tatil > izin > raporlu > geldi
        // If higher priority event exists on a day, don't count lower priority events
        let hasResmiTatil = dayEvents.some(event => event.isAllDay && event.type === 'resmi-tatil');
        const hasIzin = dayEvents.some(event => event.isAllDay && event.type === 'izin');
        const hasRaporlu = dayEvents.some(event => event.isAllDay && event.type === 'raporlu');
        const hasGeldi = dayEvents.some(event => event.type === 'geldi');
        
        // Check if it's Sunday or Saturday for Merkez Ofis (virtual resmi tatil)
        const isSunday = currentDay.getDay() === 0; // 0 = Sunday
        const isSaturday = currentDay.getDay() === 6; // 6 = Saturday
        const isMerkezOfis = user.workLocation.name === 'Merkez Ofis';
        const isVirtualResmiTatil = isSunday || (isSaturday && isMerkezOfis);
        
        // Combine actual and virtual resmi tatil
        hasResmiTatil = hasResmiTatil || isVirtualResmiTatil;
        
        if (hasResmiTatil) {
          resmiTatil++;
          offDays++;
        } else if (hasIzin) {
          izin++;
          offDays++;
        } else if (hasRaporlu) {
          raporlu++;
          offDays++;
        } else if (hasGeldi) {
          // Only count geldi if no higher priority events exist
          // geldi doesn't count as offDay, it counts as present
        }

        // Count attendance - only if no higher priority events exist
        // Priority: resmi-tatil > izin > raporlu > actual check-ins/geldi
        if (!hasResmiTatil && !hasIzin && !hasRaporlu) {
          // Only count attendance if no off-day events exist
          const geldiEvent = dayEvents.find(event => event.type === 'geldi');
          
          if (hasCheckIn || geldiEvent) {
            presentDays++;
          } else {
            // Only count as absent if no events at all
            // AND only count days up to today (including today)
            const today = new Date();
            const isUpToToday = currentDay <= new Date(today.getFullYear(), today.getMonth(), today.getDate());
            if (isUpToToday) {
              absentDays++;
            }
          }
        }
      }

      // Calculate work days as total days minus resmi tatil days
      const calculatedWorkDays = totalDays - resmiTatil;
      
      summaries.push({
        userId: user.id,
        userName: user.name,
        userEmail: user.email,
        totalDays,
        workDays: calculatedWorkDays,
        offDays,
        resmiTatil,
        izin,
        raporlu,
        presentDays,
        absentDays
      });
    });

    return summaries;
  }, [filteredUsers, checkIns, calendarEvents, currentDate, lastDayOfMonth, loadingEvents]);

  // Calculate yearly user summaries
  const yearlyUserSummaries = useMemo(() => {
    if (loadingEvents || viewMode !== 'yillik') return [];

    const summaries: YearlyUserSummary[] = [];
    const year = currentDate.getFullYear();
    const startDate = new Date(year, 0, 1); // January 1st
    const endDate = new Date(year, 11, 31); // December 31st
    const today = new Date();

    filteredUsers.forEach(user => {
      let toplamGun = 0;
      let calismaGunu = 0;
      let geldi = 0;
      let gelmedi = 0;
      let resmiTatil = 0;
      let kullanilanIzin = 0;
      let raporlu = 0;

      // Check each day of the year
      const currentDay = new Date(startDate);
      while (currentDay <= endDate) {
        // Only count days up to today
        if (currentDay > today) break;

        toplamGun++;
        
        // Get events for this day and user
        const dayEvents = calendarEvents.filter(event => {
          const eventStart = new Date(event.startDate);
          const eventEnd = new Date(event.endDate);
          
          // Reset time to start of day for proper comparison
          const startOfDay = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
          const startOfEventStart = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
          const startOfEventEnd = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
          
          const isOnDate = startOfDay >= startOfEventStart && startOfDay <= startOfEventEnd;
          const isForUser = event.scope === 'company_wide' || event.userId === user.id;
          
          // For Saturday events, only apply to Merkez Ofis users
          const isSaturday = currentDay.getDay() === 6;
          const isMerkezOfis = user.workLocation.name === 'Merkez Ofis';
          const isSaturdayEvent = isSaturday && eventStart.getDay() === 6;
          
          if (isSaturdayEvent && !isMerkezOfis) {
            return false; // Skip Saturday events for non-Merkez Ofis users
          }
          
          return isOnDate && isForUser;
        });

        // Get check-ins for this day and user (only first check-in of the day)
        const dayCheckIns = checkIns.filter(checkIn => {
          const checkInDate = new Date(checkIn.timestamp);
          return checkInDate.toDateString() === currentDay.toDateString() && checkIn.userId === user.id;
        });
        
        // Only count the first check-in of the day for this user
        const hasCheckIn = dayCheckIns.length > 0;

        // Count events by type with priority: resmi-tatil > izin > raporlu > geldi
        let hasResmiTatil = dayEvents.some(event => event.isAllDay && event.type === 'resmi-tatil');
        const hasIzin = dayEvents.some(event => event.isAllDay && event.type === 'izin');
        const hasRaporlu = dayEvents.some(event => event.isAllDay && event.type === 'raporlu');
        const hasGeldi = dayEvents.some(event => event.type === 'geldi');
        
        // Check if it's Sunday or Saturday for Merkez Ofis (virtual resmi tatil)
        const isSunday = currentDay.getDay() === 0; // 0 = Sunday
        const isSaturday = currentDay.getDay() === 6; // 6 = Saturday
        const isMerkezOfis = user.workLocation.name === 'Merkez Ofis';
        const isVirtualResmiTatil = isSunday || (isSaturday && isMerkezOfis);
        
        // Combine actual and virtual resmi tatil
        hasResmiTatil = hasResmiTatil || isVirtualResmiTatil;
        
        if (hasResmiTatil) {
          resmiTatil++;
        } else if (hasIzin) {
          kullanilanIzin++;
        } else if (hasRaporlu) {
          raporlu++;
        } else if (hasGeldi) {
          // geldi counts as present
        }

        // Count attendance - only if no higher priority events exist
        if (!hasResmiTatil && !hasIzin && !hasRaporlu) {
          const geldiEvent = dayEvents.find(event => event.type === 'geldi');
          
          if (hasCheckIn || geldiEvent) {
            geldi++;
          } else {
            gelmedi++;
          }
        }

        // Move to next day
        currentDay.setDate(currentDay.getDate() + 1);
      }

      // Calculate work days as total days minus resmi tatil days
      calismaGunu = toplamGun - resmiTatil;
      
      // Get hak edilen izin from user's pto field, default to 200 if not set
      const hakEdilenIzin = user.pto || 0;
      const kalanIzin = hakEdilenIzin - kullanilanIzin;
      
      summaries.push({
        userId: user.id,
        userName: user.name,
        userEmail: user.email,
        toplamGun,
        calismaGunu,
        geldi,
        gelmedi,
        resmiTatil,
        hakEdilenIzin,
        kullanilanIzin,
        kalanIzin,
        raporlu
      });
    });

    return summaries;
  }, [filteredUsers, checkIns, calendarEvents, currentDate, loadingEvents, viewMode]);

  // Generate calendar days for the current month only
  const calendarDays = useMemo(() => {
    const days: DayData[] = [];
    const currentDate = new Date(firstDayOfMonth);
    const today = new Date();

    while (currentDate <= lastDayOfMonth) {
      const dateStr = currentDate.toDateString();
      const dayCheckIns = checkIns.filter(checkIn => {
        const checkInDate = new Date(checkIn.timestamp);
        return checkInDate.toDateString() === dateStr;
      });

      // Get events for this day
      const dayEvents = calendarEvents.filter(event => {
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);
        const currentDay = new Date(currentDate);
        
        // Reset time to start of day for proper comparison
        const startOfDay = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
        const startOfEventStart = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
        const startOfEventEnd = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
        
        return startOfDay >= startOfEventStart && startOfDay <= startOfEventEnd;
      });
      
      // Check if it's Sunday and add it as a default resmi tatil
      const isSunday = currentDate.getDay() === 0; // 0 = Sunday
      if (isSunday) {
        // Create a virtual Sunday event
        const sundayEvent = {
          id: `sunday-${currentDate.toISOString().split('T')[0]}`,
          type: 'resmi-tatil' as const,
          title: 'Pazar Günü',
          description: 'Otomatik resmi tatil',
          startDate: new Date(currentDate),
          endDate: new Date(currentDate),
          isAllDay: true,
          scope: 'company_wide' as const,
          userId: null,
          createdBy: 'system',
          createdAt: new Date(currentDate),
          updatedAt: new Date(currentDate)
        };
        dayEvents.push(sundayEvent);
      }
      
      // Check if it's Saturday and add it as a default resmi tatil for Merkez Ofis
      const isSaturday = currentDate.getDay() === 6; // 6 = Saturday
      if (isSaturday) {
        // Create a virtual Saturday event for Merkez Ofis users
        const saturdayEvent = {
          id: `saturday-merkez-${currentDate.toISOString().split('T')[0]}`,
          type: 'resmi-tatil' as const,
          title: 'Cumartesi (Merkez)',
          description: 'Otomatik resmi tatil - Merkez Ofis',
          startDate: new Date(currentDate),
          endDate: new Date(currentDate),
          isAllDay: true,
          scope: 'user_specific' as const,
          userId: null, // Will be filtered per user in getEventsForDay
          createdBy: 'system',
          createdAt: new Date(currentDate),
          updatedAt: new Date(currentDate)
        };
        dayEvents.push(saturdayEvent);
      }

      days.push({
        date: new Date(currentDate),
        isToday: currentDate.toDateString() === today.toDateString(),
        isCurrentMonth: true,
        checkIns: dayCheckIns,
        events: dayEvents
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, [firstDayOfMonth, lastDayOfMonth, checkIns, calendarEvents]);

  // Get check-ins for a specific day and user
  const getCheckInsForDay = (date: Date, userId: string) => {
    const dateStr = date.toDateString();
    const dayCheckIns = checkIns.filter(checkIn => {
      const checkInDate = new Date(checkIn.timestamp);
      return checkInDate.toDateString() === dateStr && checkIn.userId === userId;
    });
    
    // Only return the first check-in of the day for this user
    return dayCheckIns.length > 0 ? [dayCheckIns[0]] : [];
  };

  // Get events for a specific day and user
  const getEventsForDay = (date: Date, userId: string) => {
    const events = calendarEvents.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const currentDay = new Date(date);
      
      // Reset time to start of day for proper comparison
      const startOfDay = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
      const startOfEventStart = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
      const startOfEventEnd = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
      
      const isOnDate = startOfDay >= startOfEventStart && startOfDay <= startOfEventEnd;
      const isForUser = event.scope === 'company_wide' || event.userId === userId;
      
      // For Saturday events, only apply to Merkez Ofis users
      const isSaturday = date.getDay() === 6;
      const isMerkezOfis = users.find(u => u.id === userId)?.workLocation.name === 'Merkez Ofis';
      const isSaturdayEvent = isSaturday && eventStart.getDay() === 6;
      
      if (isSaturdayEvent && !isMerkezOfis) {
        return false; // Skip Saturday events for non-Merkez Ofis users
      }
      
      // Debug logging for 4th of July
      if (date.getDate() === 4 && date.getMonth() === 6) { // July 4th
        console.log('Checking July 4th:', {
          eventTitle: event.title,
          eventStart: eventStart,
          eventEnd: eventEnd,
          currentDay: currentDay,
          startOfDay: startOfDay,
          startOfEventStart: startOfEventStart,
          startOfEventEnd: startOfEventEnd,
          isOnDate: isOnDate,
          isForUser: isForUser,
          scope: event.scope,
          userId: event.userId
        });
      }
      
      return isOnDate && isForUser;
    });
    
    // Check if it's Sunday and add it as a default resmi tatil
    const isSunday = date.getDay() === 0; // 0 = Sunday
    if (isSunday) {
      // Create a virtual Sunday event
      const sundayEvent = {
        id: `sunday-${date.toISOString().split('T')[0]}`,
        type: 'resmi-tatil' as const,
        title: 'Pazar Günü',
        description: 'Otomatik resmi tatil',
        startDate: date,
        endDate: date,
        isAllDay: true,
        scope: 'company_wide' as const,
        userId: null,
        createdBy: 'system',
        createdAt: date,
        updatedAt: date
      };
      events.push(sundayEvent);
    }
    
    // Check if it's Saturday and user is from Merkez Ofis
    const isSaturday = date.getDay() === 6; // 6 = Saturday
    const isMerkezOfis = users.find(u => u.id === userId)?.workLocation.name === 'Merkez Ofis';
    if (isSaturday && isMerkezOfis) {
      // Create a virtual Saturday event for Merkez Ofis users
      const saturdayEvent = {
        id: `saturday-merkez-${date.toISOString().split('T')[0]}`,
        type: 'resmi-tatil' as const,
        title: 'Cumartesi (Merkez)',
        description: 'Otomatik resmi tatil - Merkez Ofis',
        startDate: date,
        endDate: date,
        isAllDay: true,
        scope: 'user_specific' as const,
        userId: userId,
        createdBy: 'system',
        createdAt: date,
        updatedAt: date
      };
      events.push(saturdayEvent);
    }
    

    
    return events;
  };

  // Get attendance status for a user on a specific day
  const getAttendanceStatus = (date: Date, userId: string) => {
    const dayCheckIns = getCheckInsForDay(date, userId);
    const dayEvents = getEventsForDay(date, userId);
    
    // Check if there's a "geldi" event (admin marked as came)
    const geldiEvent = dayEvents.find(event => event.type === 'geldi');
    if (geldiEvent) {
      return 'present';
    }
    
    // If there are events, check if any are all-day events (excluding geldi)
    const allDayEvents = dayEvents.filter(event => event.isAllDay && event.type !== 'geldi');
    if (allDayEvents.length > 0) {
      return 'event';
    }
    
    // Check if it's Sunday
    const isSunday = date.getDay() === 0;
    if (isSunday) {
      return 'event';
    }
    
    // Check if it's Saturday and user is from Merkez Ofis
    const isSaturday = date.getDay() === 6;
    const isMerkezOfis = users.find(u => u.id === userId)?.workLocation.name === 'Merkez Ofis';
    if (isSaturday && isMerkezOfis) {
      return 'event';
    }
    
    if (dayCheckIns.length === 0) return 'absent';
    return 'present';
  };

  // Get event type color
  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'resmi-tatil': return 'bg-red-500';
      case 'izin': return 'bg-blue-500';
      case 'raporlu': return 'bg-yellow-500';
      case 'geldi': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Get event type label
  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'resmi-tatil': return 'RT';
      case 'izin': return 'İZ';
      case 'raporlu': return 'RA';
      case 'geldi': return 'GL';
      default: return type;
    }
  };

  // Filter events by priority: resmi-tatil > izin > raporlu > geldi
  // If higher priority event exists, don't show lower priority events for that day
  const getFilteredEvents = (events: CalendarEvent[]) => {
    const hasResmiTatil = events.some(event => event.isAllDay && event.type === 'resmi-tatil');
    const hasIzin = events.some(event => event.isAllDay && event.type === 'izin');
    const hasRaporlu = events.some(event => event.isAllDay && event.type === 'raporlu');
    const hasGeldi = events.some(event => event.type === 'geldi');
    
    // Keep non-all-day events (excluding geldi which will be handled by priority)
    const nonAllDayEvents = events.filter(event => !event.isAllDay && event.type !== 'geldi');
    
    // Apply priority for all events including geldi
    const priorityEvents = [];
    if (hasResmiTatil) {
      priorityEvents.push(...events.filter(event => event.isAllDay && event.type === 'resmi-tatil'));
    } else if (hasIzin) {
      priorityEvents.push(...events.filter(event => event.isAllDay && event.type === 'izin'));
    } else if (hasRaporlu) {
      priorityEvents.push(...events.filter(event => event.isAllDay && event.type === 'raporlu'));
    } else if (hasGeldi) {
      priorityEvents.push(...events.filter(event => event.type === 'geldi'));
    }
    
    return [...nonAllDayEvents, ...priorityEvents];
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Go to current month
  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  // Download attendance data as Excel
  const downloadExcel = () => {
    if (viewMode === 'aylik') {
      // Monthly Excel download
      const excelData = [];
      
      // Header row with dates and summary columns
      const headerRow = ['Kullanıcı', 'Email', 'Konum'];
      calendarDays.forEach(day => {
        const dateStr = day.date.toLocaleDateString('tr-TR', { 
          day: '2-digit', 
          month: '2-digit' 
        });
        const dayName = day.date.toLocaleDateString('tr-TR', { weekday: 'short' });
        headerRow.push(`${dateStr} (${dayName})`);
      });
      
      // Add summary column headers
      headerRow.push('Toplam Gün', 'Çalışma Günü', 'İzin Günü', 'Geldi', 'Gelmedi', 'Resmi Tatil', 'İzin', 'raporlu');
      excelData.push(headerRow);
      
      // Data rows for each user
      filteredUsers.forEach(user => {
        const userRow = [user.name, user.email, user.workLocation.name || 'Konum Yok'];
        
        // Add daily attendance data
        calendarDays.forEach(day => {
          const status = getAttendanceStatus(day.date, user.id);
          const checkIns = getCheckInsForDay(day.date, user.id);
          const allEvents = getEventsForDay(day.date, user.id);
          const events = getFilteredEvents(allEvents);
          
          let cellValue = '';
          if (events.length > 0) {
            cellValue = events.map(event => getEventTypeLabel(event.type)).join(', ');
          } else if (status === 'present') {
            cellValue = '✓';
          } else {
            cellValue = '✗';
          }
          
          // Add check-in time if available (only first check-in of the day)
          if (checkIns.length > 0) {
            const time = new Date(checkIns[0].timestamp).toLocaleTimeString('tr-TR', {
              hour: '2-digit',
              minute: '2-digit'
            });
            cellValue += ` [${time}]`;
          }
          
          userRow.push(cellValue);
        });
        
        // Add summary data for this user
        const userSummary = userSummaries.find(summary => summary.userId === user.id);
        if (userSummary) {
          userRow.push(
            userSummary.totalDays.toString(),
            userSummary.workDays.toString(),
            userSummary.offDays.toString(),
            userSummary.presentDays.toString(),
            userSummary.absentDays.toString(),
            userSummary.resmiTatil.toString(),
            userSummary.izin.toString(),
            userSummary.raporlu.toString()
          );
        } else {
          // Fallback if summary not found
          userRow.push('0', '0', '0', '0', '0', '0', '0', '0');
        }
        
        excelData.push(userRow);
      });
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 20 }, // User name
        { wch: 25 }, // Email
        { wch: 20 }, // Location
      ];
      
      // Add column widths for each day
      calendarDays.forEach(() => {
        colWidths.push({ wch: 12 });
      });
      
      // Add column widths for summary columns
      colWidths.push({ wch: 12 }); // Toplam Gün
      colWidths.push({ wch: 12 }); // Çalışma Günü
      colWidths.push({ wch: 12 }); // İzin Günü
      colWidths.push({ wch: 12 }); // Geldi
      colWidths.push({ wch: 12 }); // Gelmedi
      colWidths.push({ wch: 12 }); // Resmi Tatil
      colWidths.push({ wch: 12 }); // İzin
      colWidths.push({ wch: 12 }); // raporlu
      
      ws['!cols'] = colWidths;
      
      // Add worksheet to workbook
      const monthYear = currentDate.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long' 
      });
      XLSX.utils.book_append_sheet(wb, ws, `Devam_${monthYear}`);
      
      // Download the file
      const fileName = `devam_cizelgesi_${currentDate.getFullYear()}_${String(currentDate.getMonth() + 1).padStart(2, '0')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } else {
      // Yearly Excel download
      const excelData = [];
      
      // Header row for yearly summary
      const headerRow = [
        'Kullanıcı', 
        'Email', 
        'Toplam Gün', 
        'Çalışma Günü', 
        'Geldi', 
        'Gelmedi', 
        'Resmi Tatil', 
        'Hak Edilen İzin', 
        'Kullanılan İzin', 
        'Kalan İzin', 
        'Raporlu'
      ];
      excelData.push(headerRow);
      
      // Data rows for each user
      yearlyUserSummaries.forEach(summary => {
        const userRow = [
          summary.userName,
          summary.userEmail,
          summary.toplamGun.toString(),
          summary.calismaGunu.toString(),
          summary.geldi.toString(),
          summary.gelmedi.toString(),
          summary.resmiTatil.toString(),
          summary.hakEdilenIzin.toString(),
          summary.kullanilanIzin.toString(),
          summary.kalanIzin.toString(),
          summary.raporlu.toString()
        ];
        
        excelData.push(userRow);
      });
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 20 }, // User name
        { wch: 25 }, // Email
        { wch: 12 }, // Toplam Gün
        { wch: 12 }, // Çalışma Günü
        { wch: 12 }, // Geldi
        { wch: 12 }, // Gelmedi
        { wch: 12 }, // Resmi Tatil
        { wch: 12 }, // Hak Edilen İzin
        { wch: 12 }, // Kullanılan İzin
        { wch: 12 }, // Kalan İzin
        { wch: 12 }, // Raporlu
      ];
      
      ws['!cols'] = colWidths;
      
      // Add worksheet to workbook
      const year = currentDate.getFullYear();
      XLSX.utils.book_append_sheet(wb, ws, `Yillik_Devam_${year}`);
      
      // Download the file
      const fileName = `yillik_devam_cizelgesi_${year}.xlsx`;
      XLSX.writeFile(wb, fileName);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {viewMode === 'aylik' ? 'Aylık Devam Takip Çizelgesi' : 'Yıllık Devam Takip Çizelgesi'}
        </h2>
        
        {/* View Mode Toggle */}
        <div className="mb-4">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('aylik')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'aylik'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setViewMode('yillik')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'yillik'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yıllık
            </button>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          {/* Month/Year Navigation */}
          <div className="flex items-center gap-2">
            {viewMode === 'aylik' ? (
              <>
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
                  {currentDate.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}
                </h3>
                <button
                  onClick={goToNextMonth}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={goToCurrentMonth}
                  className="ml-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Bugün
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1))}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
                  {currentDate.getFullYear()}
                </h3>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1))}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="ml-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Bu Yıl
                </button>
              </>
            )}
            <button
              onClick={downloadExcel}
              className="ml-2 px-4 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel İndir
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Kullanıcı, email veya konum ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
        </div>

        {/* Search Results Info */}
        {searchTerm && (
          <div className="mb-4 text-sm text-gray-600">
            {filteredUsers.length === 0 ? (
              <span className="text-red-600">Hiç kullanıcı bulunamadı.</span>
            ) : (
              <span>
                {filteredUsers.length} kullanıcı bulundu
                {(() => {
                  const totalActiveUsers = users.filter(u => 
                    u.id !== 'admin' && 
                    u.email !== 'admin@mecon.com' && 
                    u.email !== 'muhasebe@meconyapi.com' &&
                    !u.disabled
                  ).length;
                  return filteredUsers.length !== totalActiveUsers ? 
                    ` (toplam ${totalActiveUsers} aktif kullanıcıdan)` : '';
                })()}
              </span>
            )}
          </div>
        )}



        {/* Content based on view mode */}
        {viewMode === 'aylik' ? (
          /* Monthly Attendance Table */
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                      Kullanıcı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Konum
                    </th>
                    {calendarDays.map((day, index) => (
                      <th key={index} className={`px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        day.isToday ? 'bg-blue-50' : ''
                      }`}>
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-gray-400">
                            {day.date.toLocaleDateString('tr-TR', { weekday: 'short' })}
                          </span>
                          <span className={`text-sm font-medium ${
                            day.isToday ? 'text-blue-600' : 'text-gray-900'
                          }`}>
                            {day.date.getDate()}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                        <div>
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium text-gray-900">{user.workLocation.name || 'Konum Yok'}</div>
                          <div className="text-sm text-gray-500">{user.workLocation.address || 'Adres Yok'}</div>
                        </div>
                      </td>
                      {calendarDays.map((day, dayIndex) => {
                        const status = getAttendanceStatus(day.date, user.id);
                        const checkIns = getCheckInsForDay(day.date, user.id);
                        const allEvents = getEventsForDay(day.date, user.id);
                        const events = getFilteredEvents(allEvents);
                        
                        return (
                          <td 
                            key={dayIndex} 
                            className={`px-2 py-4 text-center ${
                              day.isToday ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              {/* Show events if any */}
                              {events.length > 0 ? (
                                <div className="space-y-1">
                                  {events.map((event, idx) => (
                                    <div
                                      key={idx}
                                      className={`px-1 py-0.5 text-xs font-medium text-white rounded ${getEventTypeColor(event.type)}`}
                                      title={`${event.title}${event.description ? ` - ${event.description}` : ''}`}
                                    >
                                      {getEventTypeLabel(event.type)}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                /* Show attendance status if no events */
                                <>
                                  <div className={`w-4 h-4 rounded-full mb-1 ${
                                    status === 'present' ? 'bg-green-500' : 'bg-gray-300'
                                  }`} />
                                  {checkIns.length > 0 && (
                                    <div className="text-xs text-gray-600">
                                      <div className="font-mono">
                                        {new Date(checkIns[0].timestamp).toLocaleTimeString('tr-TR', {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Yearly Summary Table */
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kullanıcı
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Toplam Gün
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Çalışma Günü
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Geldi
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gelmedi
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resmi Tatil
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hak Edilen İzin
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kullanılan İzin
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kalan İzin
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Raporlu
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {yearlyUserSummaries.map((summary) => (
                    <tr key={summary.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{summary.userName}</div>
                          <div className="text-sm text-gray-500">{summary.userEmail}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {summary.toplamGun}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {summary.calismaGunu}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {summary.geldi}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {summary.gelmedi}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {summary.resmiTatil}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {summary.hakEdilenIzin}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {summary.kullanilanIzin}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          summary.kalanIzin >= 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {summary.kalanIzin}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {summary.raporlu}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend - Only show for monthly view */}
        {viewMode === 'aylik' && (
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Geldi</span>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
              <span>Gelmedi</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Resmi Tatil</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>İzin</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span>Raporlu</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>GL - Admin Tarafından Eklendi</span>
            </div>
          </div>
        )}

        {/* Summary Section - Only show for monthly view */}
        {showSummary && viewMode === 'aylik' && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Aylık Özet</h3>
            
            {/* Summary Table */}
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Toplam Gün
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Çalışma Günü
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İzin Günü
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Geldi
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gelmedi
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Resmi Tatil
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İzin
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        raporlu
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userSummaries.map((summary) => (
                      <tr key={summary.userId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{summary.userName}</div>
                            <div className="text-sm font-medium text-gray-500">{summary.userEmail}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {summary.totalDays}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {summary.workDays}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {summary.offDays}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {summary.presentDays}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {summary.absentDays}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {summary.resmiTatil}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {summary.izin}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            {summary.raporlu}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAttendanceTimesheet; 